/**
 * AI创作中心 - 模型配置
 * 维护生图/生视频模型的映射关系、参数格式、分辨率选项等
 */

// ==================== 生图模型 ====================

export const imageModels = [
  {
    label: 'seedream5.0',
    value: 'M-0001-0001',
    desc: '指令响应更精准，生成效果更智能',
    sizes: ['auto', '2K', '3K', '4K'],
    // 乘积范围：2560*1440 ~ 4096*4096
    productRange: [2560 * 1440, 4096 * 4096],
  },
  {
    label: 'seedream4.5',
    value: 'M-0001-0002',
    desc: '画面全面优化，指令响应能力再次提升',
    sizes: ['auto', '2K', '4K'],
    productRange: [2560 * 1440, 4096 * 4096],
  },
  // {
  //   label: 'seedream4.0',
  //   value: 'M-0001-0003',
  //   desc: '提供基础的图像，整体和一致性保持',
  //   sizes: ['auto', '1K', '2K', '4K'],
  //   productRange: [1280 * 720, 4096 * 4096],
  // },
  {
    label: 'wan2.7-image-pro',
    value: 'M-0002-0001',
    desc: '全能参考，让角色演绎起来',
    sizes: ['auto', '1K', '2K', '4K'],
    productRange: [768 * 768, 4096 * 4096],
  },
  {
    label: 'wan2.7-image',
    value: 'M-0002-0002',
    desc: '多镜头叙事，动态时长生成',
    sizes: ['auto', '1K', '2K'],
    productRange: [768 * 768, 2048 * 2048],
  },
  {
    label: 'qwen-image-2.0-pro',
    value: 'M-0002-0004',
    desc: '强化一致性，风格与图文描述',
    sizes: ['auto', '1K', '2K'],
    productRange: [1024 * 1024, 2048 * 2048],
  },
  {
    label: 'qwen-image-2.0',
    value: 'M-0002-0005',
    desc: '提供基础的图像，整体和一致性保持',
    sizes: ['auto', '1K', '2K'],
    productRange: [1024 * 1024, 2048 * 2048],
  },
  {
    label: 'gpt-5.5(上下文)',
    value: 'M-0003-0001',
    desc: '极致细节，真实自然的表达',
    hasSetting: true, // 支持 setting 配置 (quality, lastTaskId)
    fixedQuantity: 1, // 固定生成张数，隐藏张数选择器
    multiple: 16, // 宽高需为 16 的倍数
    productRange: [655360, 8294400],
    sizes: ['auto', '1K', '2K', '4K'],
    qualityOptions: [
      { label: '自动', value: 'auto' },
      { label: '低', value: 'low' },
      { label: '中', value: 'medium' },
      { label: '高', value: 'high' },
    ],
  },
  {
    label: 'gpt-image-2',
    value: 'M-0003-0002',
    desc: '极致细节，真实自然的表达',
    hasSetting: true, // 支持 setting 配置 (quality, lastTaskId)
    multiple: 16, // 宽高需为 16 的倍数
    productRange: [655360, 8294400],
    sizes: ['auto', '1K', '2K', '4K'],
    qualityOptions: [
      { label: '自动', value: 'auto' },
      { label: '低', value: 'low' },
      { label: '中', value: 'medium' },
      { label: '高', value: 'high' },
    ],
  },
  {
    label: 'nano-banana-2-lite',
    value: 'M-0005-0001',
    desc: '轻量级生图模型',
    sizes: ['1K'],
  },
  {
    label: 'nano-banana-2',
    value: 'M-0005-0002',
    desc: '高性价比生图模型',
    sizes: ['1K', '2K', '4K'],
  },
];

/**
 * 校验自定义宽高是否符合模型限制
 * 文档中的范围（如 768*768 到 2048*2048）指的是宽*高乘积的范围
 * @param {Object} model - 生图模型配置
 * @param {number} width - 宽
 * @param {number} height - 高
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateImageCustomSize(model, width, height) {
  if (!model) {
    return { valid: false, message: '未找到模型配置' };
  }
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return { valid: false, message: '请输入有效的宽高' };
  }

  // 使用乘积范围校验
  if (model.productRange && model.productRange.length === 2) {
    const [minProduct, maxProduct] = model.productRange;
    const product = w * h;
    if (product < minProduct || product > maxProduct) {
      return {
        valid: false,
        message: `宽*高乘积需在 ${minProduct} ~ ${maxProduct} 之间（当前 ${product}）`,
      };
    }
  }

  return { valid: true };
}

// 生图分辨率选项
export const imageSizeOptions = [
  { label: '自动', value: 'auto' },
  { label: '1K', value: '1K' },
  { label: '2K', value: '2K' },
  { label: '3K', value: '3K' },
  { label: '4K', value: '4K' },
];

// 尺寸对应的基准像素（以短边为基准）
const sizeBasePixels = {
  '1K': 1024,
  '2K': 2048,
  '3K': 3072,
  '4K': 4096,
};

// 所有支持的比例
export const allRatios = [
  '21:9',
  '16:9',
  '3:2',
  '4:3',
  '1:1',
  '3:4',
  '2:3',
  '9:16',
];

// 比例对应的宽高比数值
const ratioMap = {
  '21:9': 21 / 9,
  '16:9': 16 / 9,
  '3:2': 3 / 2,
  '4:3': 4 / 3,
  '1:1': 1,
  '3:4': 3 / 4,
  '2:3': 2 / 3,
  '9:16': 9 / 16,
};

/**
 * 根据尺寸和比例计算宽高
 * @param {string} size - 尺寸 (1K/2K/3K/4K)
 * @param {string} ratio - 比例
 * @param {number} multiple - 宽高需对齐的倍数（如 16），默认 1 表示不限制
 * @returns {{ width: number, height: number }}
 */
export function sizeRatioToWidthHeight(size, ratio, multiple = 1) {
  const base = sizeBasePixels[size] || 2048;
  const ratioValue = ratioMap[ratio] || 1;

  let width, height;
  if (ratioValue >= 1) {
    // 横屏或方形：短边 = base
    height = base;
    width = Math.round(base * ratioValue);
  } else {
    // 竖屏：短边 = base
    width = base;
    height = Math.round(base / ratioValue);
  }

  // 若配置了倍数（如 gpt 系列要求 16 的倍数），将宽高向下微调为 multiple 的倍数
  if (multiple > 1) {
    width = Math.floor(width / multiple) * multiple;
    height = Math.floor(height / multiple) * multiple;
  }

  return { width, height };
}

/**
 * 根据模型配置过滤可用的 size 和 ratio 组合
 * @param {Object} model - 生图模型配置
 * @returns {{ availableSizes: string[], availableRatios: string[] }}
 */
export function getAvailableSizeAndRatios(model) {
  if (!model) {
    return { availableSizes: ['1K', '2K', '4K'], availableRatios: allRatios };
  }

  // 获取模型支持的 size
  const modelSizes = model.sizes || ['1K', '2K', '4K'];

  // 获取模型的乘积范围限制
  const productRange = model.productRange;
  if (!productRange || productRange.length !== 2) {
    return { availableSizes: modelSizes, availableRatios: allRatios };
  }

  const [minProduct, maxProduct] = productRange;

  // 过滤出有效的 size + ratio 组合
  const validSizes = new Set();
  const validRatios = new Set();

  for (const size of modelSizes) {
    for (const ratio of allRatios) {
      const { width, height } = sizeRatioToWidthHeight(
        size,
        ratio,
        model.multiple || 1,
      );
      const product = width * height;

      // 检查乘积是否在范围内
      if (product >= minProduct && product <= maxProduct) {
        validSizes.add(size);
        validRatios.add(ratio);
      }
    }
  }

  return {
    availableSizes: Array.from(validSizes),
    availableRatios: Array.from(validRatios),
  };
}

/**
 * 根据选择的 size 过滤可用的 ratio（动态联动）
 * @param {Object} model - 生图模型配置
 * @param {string} selectedSize - 当前选择的 size
 * @returns {string[]} 可用的 ratio 列表
 */
export function getAvailableRatiosForSize(model, selectedSize) {
  if (!model || !selectedSize) {
    return allRatios;
  }

  const productRange = model.productRange;
  if (!productRange || productRange.length !== 2) {
    return allRatios;
  }

  const [minProduct, maxProduct] = productRange;
  const validRatios = [];

  for (const ratio of allRatios) {
    const { width, height } = sizeRatioToWidthHeight(
      selectedSize,
      ratio,
      model.multiple || 1,
    );
    const product = width * height;

    if (product >= minProduct && product <= maxProduct) {
      validRatios.push(ratio);
    }
  }

  return validRatios;
}

/**
 * 根据选择的 ratio 过滤可用的 size（动态联动）
 * @param {Object} model - 生图模型配置
 * @param {string} selectedRatio - 当前选择的 ratio
 * @returns {string[]} 可用的 size 列表
 */
export function getAvailableSizesForRatio(model, selectedRatio) {
  if (!model || !selectedRatio) {
    return ['1K', '2K', '4K'];
  }

  const modelSizes = model.sizes || ['1K', '2K', '4K'];
  const productRange = model.productRange;
  if (!productRange || productRange.length !== 2) {
    return modelSizes;
  }

  const [minProduct, maxProduct] = productRange;
  const validSizes = [];

  for (const size of modelSizes) {
    const { width, height } = sizeRatioToWidthHeight(
      size,
      selectedRatio,
      model.multiple || 1,
    );
    const product = width * height;

    if (product >= minProduct && product <= maxProduct) {
      validSizes.push(size);
    }
  }

  return validSizes;
}

// 生图数量选项
export const imageQuantityOptions = [
  { label: '1张', value: 1 },
  { label: '2张', value: 2 },
  { label: '3张', value: 3 },
  { label: '4张', value: 4 },
];

// ==================== 聊天对话模型 ====================

export const chatModels = [
  {
    label: 'GPT-5.5',
    value: 'M-0004-0001',
    attachments: true,
    desc: 'OpenAI 最新语言模型',
  },
  {
    label: 'doubao-seed-2.1-pro',
    value: 'M-0004-0002',
    attachments: true,
    desc: '豆包专业版语言模型',
  },
  {
    label: 'doubao-seed-2.1-turbo',
    value: 'M-0004-0003',
    attachments: true,
    desc: '豆包快速版语言模型',
  },
  // {
  //   label: 'doubao-seed-2.0-pro',
  //   value: 'M-0004-0004',
  //   desc: '豆包2.0专业版语言模型',
  // },
  {
    label: 'deepseek-v4-pro',
    value: 'M-0004-0007',
    attachments: false,
    desc: 'DeepSeek 专业版语言模型',
  },
  {
    label: 'deepseek-v4-flash',
    value: 'M-0004-0008',
    attachments: false,
    desc: 'DeepSeek 快速版语言模型',
  },
  {
    label: 'glm-5.2',
    value: 'M-0004-0009',
    attachments: false,
    desc: '智谱 GLM 语言模型',
  },
];

// ==================== 生视频模型 ====================

export const videoModels = [
  {
    label: 'doubao-seedance-2.0',
    value: 'M-0001-1001',
    desc: '极致细节，真实自然的表达',
    resolutions: ['480p', '720p', '1080p', '4k'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    durationRange: [4, 15],
    // 支持首尾帧（与参考素材互斥）
    hasFrames: true,
    // 支持参考素材（与首尾帧互斥）
    hasReferences: {
      images: 9, // 最多9张参考图片
      videos: 3, // 最多3个参考视频
      audios: 3, // 最多3个参考音频
    },
  },
  {
    label: 'doubao-seedance-2.0-fast',
    value: 'M-0001-1002',
    desc: '快速生成，高效表达',
    resolutions: ['480p', '720p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    durationRange: [4, 15],
    // 支持首尾帧（与参考素材互斥）
    hasFrames: true,
    // 支持参考素材（与首尾帧互斥）
    hasReferences: {
      images: 9,
      videos: 3,
      audios: 3,
    },
  },
  {
    label: 'wan2.7文生视频',
    value: 'M-0002-1001',
    desc: '全能参考，让角色演绎起来',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 15],
  },
  {
    label: 'wan2.7图生视频',
    value: 'M-0002-1002',
    desc: '首尾帧控制，精准演绎',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 15],
    hasFrames: true, // 支持首尾帧
  },
  {
    label: 'wan2.7参考生视频',
    value: 'M-0002-1003',
    desc: '多参考素材，自由组合',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 10], // 有视频时 2-10 秒
    // 支持参考素材（不限制数量）
    hasReferences: {
      images: true,
      videos: true,
      audios: true,
    },
  },
  {
    label: 'wan2.7视频编辑',
    value: 'M-0002-1004',
    desc: '视频编辑，智能修改',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 15],
    // 仅支持编辑视频（单个）
    hasReferences: {
      videos: 1, // 只能上传1个视频
    },
  },
  {
    label: 'happyhorse文生视频',
    value: 'M-0002-1005',
    desc: '极致细节，真实自然的表达',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
  },
  {
    label: 'happyhorse图生视频',
    value: 'M-0002-1006',
    desc: '首尾帧控制，精准演绎',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
    hasFrames: true, // 支持首尾帧
  },
  {
    label: 'happyhorse参考生视频',
    value: 'M-0002-1007',
    desc: '多参考素材，自由组合',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
    // 支持参考素材
    hasReferences: {
      images: 9,
      videos: false,
      audios: false,
    },
  },
  {
    label: 'happyhorse视频编辑',
    value: 'M-0002-1008',
    desc: '视频编辑，智能修改',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
    // 仅支持编辑视频（单个）
    hasReferences: {
      videos: 1, // 只能上传1个视频
    },
  },
];

// 视频分辨率选项
export const videoResolutionOptions = [
  { label: '480p', value: '480p' },
  { label: '720p', value: '720p' },
  { label: '1080p', value: '1080p' },
  { label: '4k', value: '4k' },
];

// ==================== 工具函数 ====================

/**
 * 构建生图模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {number} params.number - 数量
 * @param {string} params.size - 分辨率 (1K/2K/3K/4K)，与 width/height 互斥
 * @param {number} params.width - 宽度
 * @param {number} params.height - 高度
 * @param {string[]} params.images - 参考图URL数组
 * @param {Object} params.setting - 特殊配置 (gpt-5.5 需要)
 * @returns {string} JSON字符串
 */
export function buildImagePromptSetting(params) {
  const { modelCode, prompt, number, size, width, height, images, setting } =
    params;
  const payload = {
    modelCode,
    prompt,
    number,
  };

  // width/height 优先（由比例+尺寸计算得出），没有则用 size
  if (width && height) {
    payload.width = width;
    payload.height = height;
  } else if (size) {
    payload.size = size;
  }

  if (images && images.length > 0) {
    payload.images = images;
  }

  if (setting) {
    payload.setting = setting;
  }

  return JSON.stringify(payload);
}

/**
 * 构建聊天对话模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {string[]} params.attachments - 上传的图片URL数组
 * @returns {string} JSON字符串
 */
export function buildChatPromptSetting(params) {
  const { modelCode, prompt, attachments } = params;
  const payload = {
    modelCode,
    prompt,
  };
  if (attachments && attachments.length > 0) {
    payload.attachments = attachments;
  }
  return JSON.stringify(payload);
}

/**
 * 构建生视频模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {string} params.ratio - 比例
 * @param {string} params.duration - 时长(秒)
 * @param {string} params.resolution - 分辨率
 * @param {string} params.firstFrameImage - 首帧图片URL
 * @param {string} params.lastFrameImage - 尾帧图片URL
 * @param {string[]} params.referenceVideos - 参考视频URL数组
 * @param {string[]} params.referenceImages - 参考图片URL数组
 * @param {string[]} params.referenceAudios - 参考音频URL数组
 * @param {Object} params.setting - 特殊配置
 * @returns {string} JSON字符串
 */
export function buildVideoPromptSetting(params) {
  const {
    modelCode,
    prompt,
    ratio,
    duration,
    resolution,
    firstFrameImage,
    lastFrameImage,
    referenceVideos,
    referenceImages,
    referenceAudios,
    setting,
  } = params;

  const payload = {
    modelCode,
    prompt,
    ratio,
    duration,
    resolution,
  };

  if (firstFrameImage) {
    payload.firstFrameImage = firstFrameImage;
  }
  if (lastFrameImage) {
    payload.lastFrameImage = lastFrameImage;
  }
  if (referenceVideos && referenceVideos.length > 0) {
    payload.referenceVideos = referenceVideos;
  }
  if (referenceImages && referenceImages.length > 0) {
    payload.referenceImages = referenceImages;
  }
  if (referenceAudios && referenceAudios.length > 0) {
    payload.referenceAudios = referenceAudios;
  }
  if (setting) {
    payload.setting = setting;
  }

  return JSON.stringify(payload);
}

/**
 * 根据时长范围生成时长选项
 * @param {number[]} durationRange - 时长范围 [min, max]
 * @returns {Array} 时长选项数组
 */
export function generateDurationOptions(durationRange) {
  if (!durationRange || durationRange.length !== 2) {
    return [
      { label: '4s', value: '4' },
      { label: '5s', value: '5' },
      { label: '6s', value: '6' },
      { label: '7s', value: '7' },
      { label: '8s', value: '8' },
      { label: '9s', value: '9' },
      { label: '10s', value: '10' },
      { label: '11s', value: '11' },
      { label: '12s', value: '12' },
      { label: '13s', value: '13' },
      { label: '14s', value: '14' },
      { label: '15s', value: '15' },
    ];
  }
  const [min, max] = durationRange;
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push({ label: `${i}s`, value: String(i) });
  }
  return options;
}

/**
 * 根据模型代码查找模型配置
 */
export function findImageModelByCode(code) {
  return imageModels.find((m) => m.value === code);
}

export function findVideoModelByCode(code) {
  return videoModels.find((m) => m.value === code);
}

export function findChatModelByCode(code) {
  return chatModels.find((m) => m.value === code);
}
